/**
 * objectsBrowseLevel.ts
 *
 * The scene editor's object library opens on cards rather than on a request,
 * and only fetches once a filter or a search phrase narrows the list. An
 * unnarrowed first page of the whole library (or of all of Bad) cost a
 * request on every library open and was rarely what anyone was looking for.
 * Clearing the search and resetting the filters to "all" brings the cards back.
 *
 * - No search, no filter: the main categories as cards.
 * - Only the bathroom category: its subcategories as cards. Bathroom holds
 *   orders of magnitude more objects than every other category, so it is the
 *   only one narrowed further before fetching. Any other category fetches and
 *   shows all its objects; its subcategories stay available in
 *   FilterAndSortObject's subcategory select.
 *
 * The cards are for browsing the public catalog only. Private objects are a
 * short list of the organization's own uploads, fetched as soon as the
 * visibility filter switches to private.
 *
 * What never counts as narrowing: the locale FilterAndSortObject adds to
 * every query.
 *
 * The materials tab follows the same rule (getMaterialsBrowseLevel), with
 * one level only: materials have no subcategories. Its one exception is the
 * colors category: it never fetches, but shows the hardcoded HEGIAS color
 * palette (utils/colorPresets) as virtual color materials instead.
 */

import PageQuery from "common/types/PageQuery";
import { SGVSB_DEFAULT_CATEGORY } from "common/constants/sgvsb";

/** Which cards the library shows instead of results; null means fetch. */
export type ObjectsBrowseLevel = "categories" | "subcategories" | null;

/** The one category whose subcategories are offered as cards. */
export const CATEGORY_WITH_SUBCATEGORY_CARDS = SGVSB_DEFAULT_CATEGORY;

const NON_NARROWING_FILTERS = new Set(["isPublic", "isPrivate", "locale", "category"]);

// Empty entries are no choice: the selects' "All" option is the value "",
// which used to arrive as category [""].
const isSet = (value: unknown): boolean => {
	if (value === undefined || value === null || value === "" || value === false) return false;
	if (Array.isArray(value)) return value.some(isSet);
	return true;
};

/** Which cards the materials tab shows instead of results; null means fetch. */
export type MaterialsBrowseLevel = "categories" | "colors" | null;

/** The material category answered by the color palette, not the server. */
export const COLORS_CATEGORY = "color";

/**
 * The chosen categories, or null when the query is narrowed some other way —
 * a search phrase, private visibility, any other filter — and must be fetched.
 */
function browsableCategories(query: PageQuery): string[] | null {
	if (query.search?.text?.trim()) return null;

	const filters = query.filters || {};
	if (filters.isPrivate === true) return null;
	const narrowed = Object.keys(filters).some(
		(key) => !NON_NARROWING_FILTERS.has(key) && isSet(filters[key]),
	);
	if (narrowed) return null;

	return (filters.category || []).filter(isSet);
}

export function getMaterialsBrowseLevel(query: PageQuery): MaterialsBrowseLevel {
	// Public colors are the palette, whatever else is set: a search phrase
	// filters the palette by hex code instead of reaching the server. The
	// organization's own (private) color materials still come from the server.
	const filters = query.filters || {};
	const chosen = (filters.category || []).filter(isSet);
	if (filters.isPrivate !== true && chosen.length === 1 && chosen[0] === COLORS_CATEGORY) {
		return "colors";
	}

	const category = browsableCategories(query);
	return category && category.length === 0 ? "categories" : null;
}

export function getObjectsBrowseLevel(query: PageQuery): ObjectsBrowseLevel {
	const category = browsableCategories(query);
	if (!category) return null;
	if (category.length === 0) return "categories";
	if (category.length === 1 && category[0] === CATEGORY_WITH_SUBCATEGORY_CARDS) {
		return "subcategories";
	}
	return null;
}
